import { eventTracker } from 'app/modules/Segment';
import {
  buildJusfyEventsContext,
  buildJusfyEventsProperties,
  GENERAL_LIBRARY_NAME,
} from 'app/modules/Segment/buildJusfyEvent';
import {
  FeatureEnum,
  GroupIdEnum,
  TContext,
  TProperties,
} from 'app/modules/Segment/types';
import { MENU_VERSION_VALUES } from 'app/services/userPreferences/types';

const EVENTS = {
  MENU_CATEGORY_EXPANDED: 'menu_category_expanded',
  MENU_ITEM_SELECTED: 'menu_item_selected',
  MENU_NEW_TAG_VIEWED: 'menu_new_tag_viewed',
  MENU_NEW_TAG_CLICKED: 'menu_new_tag_clicked',
  MENU_PROFILE_MGM_CLICKED: 'menu_profile_mgm_clicked',
  NOTIFICATION_TAG_VIEWED: 'notification_tag_viewed',
  NOTIFICATION_TAG_CLICKED: 'notification_tag_clicked',
  NOTIFICATION_LIST_OPENED: 'notification_list_opened',
} as const;

const drawerContext: TContext = buildJusfyEventsContext({
  component: 'drawer-component',
});

export function trackMenuCategoryExpanded(categoryName: string): void {
  const event = EVENTS.MENU_CATEGORY_EXPANDED;
  eventTracker.track({
    event,
    properties: buildJusfyEventsProperties({
      event,
      feature: FeatureEnum.DASHBOARD,
      eventMeaning:
        'Disparado quando o usuário expande um agrupamento do menu (ex.: Processos, Clientes). Propriedades: `menu_category`, `menu_version`.',
      relevantUserActivity: false,
      extraProperties: {
        menu_category: categoryName,
        menu_version: MENU_VERSION_VALUES.v2,
      },
    }),
    context: drawerContext,
  });
}

export function trackMenuItemSelected(params: {
  groupId: GroupIdEnum;
  itemName: string;
  categoryParent: string | null;
}): void {
  const event = EVENTS.MENU_ITEM_SELECTED;
  eventTracker.track({
    event,
    properties: buildJusfyEventsProperties({
      event,
      feature: FeatureEnum.DASHBOARD,
      eventMeaning:
        'Disparado ao clicar em um link de ferramenta dentro do menu. Propriedades: `item_name`, `category_parent`, `menu_version`.',
      relevantUserActivity: true,
      extraProperties: {
        item_name: params.itemName,
        category_parent: params.categoryParent,
        menu_version: MENU_VERSION_VALUES.v2,
      },
    }),
    context: {
      groupId: params.groupId,
      library: {
        name: GENERAL_LIBRARY_NAME,
        componente: 'button-component',
      },
    },
  });
}

export function trackMenuNewTagViewed(itemName: string): void {
  const event = EVENTS.MENU_NEW_TAG_VIEWED;

  eventTracker.track({
    event,
    properties: buildJusfyEventsProperties({
      event,
      feature: FeatureEnum.DASHBOARD,
      eventMeaning:
        'Disparado quando o badge “Novo” do menu é exibido ao usuário. Propriedades: `item_name`, `menu_version`.',
      relevantUserActivity: false,
      extraProperties: {
        item_name: itemName,
        menu_version: MENU_VERSION_VALUES.v2,
      },
    }),
    context: buildJusfyEventsContext({ component: 'badge-component' }),
  });
}

export function trackMenuNewTagClicked(itemName: string): void {
  const event = EVENTS.MENU_NEW_TAG_CLICKED;

  eventTracker.track({
    event,
    properties: buildJusfyEventsProperties({
      event,
      feature: FeatureEnum.DASHBOARD,
      eventMeaning:
        'Disparado ao clicar no item do menu que contém o badge “Novo”. Propriedades: `item_name`, `menu_version`.',
      relevantUserActivity: true,
      extraProperties: {
        item_name: itemName,
        menu_version: MENU_VERSION_VALUES.v2,
      },
    }),
    context: buildJusfyEventsContext({ component: 'badge-component' }),
  });
}

export function trackMenuProfileMgmClicked(): void {
  const event = EVENTS.MENU_PROFILE_MGM_CLICKED;

  eventTracker.track({
    event,
    properties: buildJusfyEventsProperties({
      event,
      feature: FeatureEnum.NOT_FEATURE,
      eventMeaning:
        'Disparado ao clicar em “Indique e ganhe pontos!” no menu Perfil (popover desktop ou footer mobile v2).',
      relevantUserActivity: true,
      extraProperties: {
        menu_version: MENU_VERSION_VALUES.v2,
      },
    }),
    context: buildJusfyEventsContext({ component: 'button-component' }),
  });
}

export function trackNotificationTagViewed(itemName: string): void {
  const event = EVENTS.NOTIFICATION_TAG_VIEWED;

  eventTracker.track({
    event,
    properties: buildJusfyEventsProperties({
      event,
      feature: FeatureEnum.NOT_FEATURE,
      eventMeaning:
        'Disparado quando o badge de notificações do menu é exibido ao usuário. Propriedades: `item_name`, `menu_version`.',
      relevantUserActivity: false,
      extraProperties: {
        item_name: itemName,
        menu_version: MENU_VERSION_VALUES.v2,
      },
    }),
    context: buildJusfyEventsContext({ component: 'badge-component' }),
  });
}

export function trackNotificationTagClicked(itemName: string): void {
  const event = EVENTS.NOTIFICATION_TAG_CLICKED;

  eventTracker.track({
    event,
    properties: buildJusfyEventsProperties({
      event,
      feature: FeatureEnum.NOT_FEATURE,
      eventMeaning:
        'Disparado ao clicar no item do menu que contém o badge de notificações. Propriedades: `item_name`, `menu_version`.',
      relevantUserActivity: true,
      extraProperties: {
        item_name: itemName,
        menu_version: MENU_VERSION_VALUES.v2,
      },
    }),
    context: buildJusfyEventsContext({ component: 'badge-component' }),
  });
}

export function trackNotificationListOpened(
  extraProperties: Partial<TProperties> = {},
): void {
  const event = EVENTS.NOTIFICATION_LIST_OPENED;

  eventTracker.track({
    event,
    properties: buildJusfyEventsProperties({
      event,
      feature: FeatureEnum.NOT_FEATURE,
      eventMeaning:
        'Disparado quando o usuário abre a lista/painel de notificações.',
      relevantUserActivity: true,
      extraProperties,
    }),
    context: buildJusfyEventsContext({ component: 'notification-list-component' }),
  });
}
